import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UnitOfMeasureOption } from '../../../../shared/services/sage-integration.model';
import {
  FINISHED_GOOD_TYPE_ID,
  FixMatchChoice,
  fixMatchNoun,
  FixMatchOption,
  FixMatchType,
  pickerOptions,
  recordLine,
  SageLinkAddRequest,
  SageLinkMoveRequest,
  SageLinkRow,
  swapOffered,
  whatWillChange,
} from '../../../../shared/services/sage-fix-matches.model';

@Component({
  selector: 'app-fix-match-change',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fix-match-change.component.html',
  styleUrls: ['../onboarding-wizard/sage-wizard.shared.css', './fix-match-change.component.css'],
})
export class FixMatchChangeComponent implements OnChanges {
  private static nextId = 0;
  // The web panel and the phone sheet can both be in the page, so field ids can't be fixed.
  readonly uid = `fix-match-${++FixMatchChangeComponent.nextId}`;

  @Input() type: FixMatchType = 'Item';
  @Input({ required: true }) changing!: SageLinkRow;
  @Input() rows: SageLinkRow[] = [];
  @Input() units: UnitOfMeasureOption[] = [];
  @Input() saving = false;
  @Input() error: string | null = null;
  /** Off where the panel header shows the Sage record instead. */
  @Input() showRecord = true;

  @Output() move = new EventEmitter<SageLinkMoveRequest>();
  @Output() addAsNew = new EventEmitter<SageLinkAddRequest>();
  @Output() cancel = new EventEmitter<void>();

  readonly itemTypes = [
    { id: 1, label: 'Raw material' },
    { id: FINISHED_GOOD_TYPE_ID, label: 'Finished good' },
  ];

  query = '';
  targetId: number | null = null;
  addingNew = false;
  swap = true;
  code = '';
  name = '';
  itemTypeId = 1;
  uomId: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    const changing = changes['changing'];
    if (changing && changing.previousValue?.aivorRecordId !== this.changing?.aivorRecordId) this.reset();
  }

  private reset(): void {
    this.query = '';
    this.targetId = null;
    this.addingNew = false;
    this.swap = true;
    this.code = this.changing?.originalSageCode ?? '';
    this.name = this.changing?.originalSageName ?? '';
    this.itemTypeId = 1;
    this.uomId = null;
  }

  readonly trackOption = (_: number, option: FixMatchOption) => option.row.aivorRecordId;

  get recordLine(): string {
    return recordLine(this.changing);
  }

  get noun(): string {
    return fixMatchNoun(this.type);
  }

  get isItem(): boolean {
    return this.type === 'Item';
  }

  /** A Sage service always comes into Aivor as a service. */
  get isService(): boolean {
    return this.changing.originalSageIsService === true;
  }

  get picker() {
    return pickerOptions(this.rows, this.changing, this.type, this.query);
  }

  get target(): SageLinkRow | null {
    return this.targetId == null ? null : (this.rows.find((r) => r.aivorRecordId === this.targetId) ?? null);
  }

  get showSwap(): boolean {
    return !this.addingNew && swapOffered(this.changing, this.target, this.type);
  }

  get newComplete(): boolean {
    return !!this.name.trim() && (!this.isItem || !!this.code.trim());
  }

  get choice(): FixMatchChoice | null {
    if (this.addingNew) {
      return this.newComplete
        ? { kind: 'new', code: this.code.trim(), name: this.name.trim(), itemTypeId: this.isService ? null : this.itemTypeId }
        : null;
    }
    const target = this.target;
    return target ? { kind: 'move', target, swap: this.showSwap && this.swap } : null;
  }

  get lines(): string[] {
    const choice = this.choice;
    return choice ? whatWillChange(this.type, this.changing, choice) : [];
  }

  get canSave(): boolean {
    return !this.saving && this.choice != null;
  }

  get saveLabel(): string {
    if (this.saving) return 'Saving…';
    return this.addingNew ? 'Add to Aivor' : 'Save link';
  }

  pick(row: SageLinkRow): void {
    this.targetId = row.aivorRecordId;
    this.addingNew = false;
    this.swap = true;
  }

  pickNew(): void {
    this.addingNew = true;
    this.targetId = null;
  }

  selectUnit(value: string): void {
    this.uomId = value ? Number(value) : null;
  }

  save(): void {
    const choice = this.choice;
    const sageRecordId = this.changing.sageRecordId;
    if (!this.canSave || !choice || sageRecordId == null) return;

    if (choice.kind === 'move') {
      this.move.emit({ sageRecordId, aivorRecordId: choice.target.aivorRecordId, swap: choice.swap });
      return;
    }

    this.addAsNew.emit({
      sageRecordId,
      code: this.isItem ? choice.code : null,
      name: choice.name,
      itemTypeId: this.isItem ? choice.itemTypeId : null,
      uomId: this.isItem ? this.uomId : null,
    });
  }
}
